import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import * as repo from '../data/rolesRepository';
import { rolesErrorMessage } from '../utils/rolesErrors';

export const rolesKeys = {
  all: ['roles'] as const,
  overview: ['roles', 'overview'] as const,
  catalog: ['roles', 'catalog'] as const,
  permissions: (roleId: string) => ['roles', 'permissions', roleId] as const,
  assignments: (roleId: string) => ['roles', 'assignments', roleId] as const,
};

export function useRolesOverview() {
  return useQuery({ queryKey: rolesKeys.overview, queryFn: repo.getRolesOverview });
}

export function usePermissionCatalog() {
  return useQuery({ queryKey: rolesKeys.catalog, queryFn: repo.getPermissionCatalog, staleTime: 10 * 60 * 1000 });
}

export function useRolePermissionCodes(roleId: string | undefined) {
  return useQuery({
    queryKey: rolesKeys.permissions(roleId ?? ''),
    queryFn: () => repo.getRolePermissionCodes(roleId as string),
    enabled: !!roleId,
  });
}

export function useRoleAssignments(roleId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: rolesKeys.assignments(roleId ?? ''),
    queryFn: () => repo.getRoleAssignments(roleId as string),
    enabled: !!roleId && enabled,
  });
}

/**
 * Mutation that toasts errors in Spanish and refreshes every roles query plus the current
 * user's own permissions (an edit can change what this user can do).
 */
export function useRolesMutation<TArgs, TResult = unknown>(
  fn: (args: TArgs) => Promise<TResult>,
  options?: { success?: string },
) {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      if (options?.success) toast({ title: options.success });
      void qc.invalidateQueries({ queryKey: rolesKeys.all });
      void qc.invalidateQueries({ queryKey: ['current_user_permissions'] });
    },
    onError: (err) => toast({ title: rolesErrorMessage(err), variant: 'destructive' }),
  });
}
